import { Combobox } from '@/component'
import { cn } from '@/lib/utils'
import React, { useState, createContext, useContext } from 'react'
import { Button } from '@/component/ui/button'
import { Command, CommandGroup, CommandItem, CommandList } from '@/component/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/component/ui/popover'
import { Check, ChevronDown } from 'lucide-react'
import { TriangleDownIcon, TriangleUpIcon } from '@radix-ui/react-icons'
import { Separator } from '@/component/ui/separator'

export interface SortItem {
  label: string | React.ReactNode
  sortField: string
  sortType: ('desc' | 'asc')[]
  priceType?: string
}

interface SortBaseProps {
  sorts: SortItem[]
  value?: {
    field: string
    type: 'desc' | 'asc'
  }
  disabled?: boolean
  onChange?: (field: string, type: 'desc' | 'asc') => void
  className?: string
  placeholder?: string
  withPlaceholder?: boolean
}

type SortContextType = {
  value?: {
    field: string
    type: 'desc' | 'asc'
  }
  onChange?: (field: string, type: 'desc' | 'asc') => void
}

const SortContext = createContext<SortContextType>({})

const onSortClick = (
  item: SortItem,
  value?: { field: string; type: 'desc' | 'asc' },
  onChange?: (field: string, type: 'desc' | 'asc') => void,
) => {
  if (item.sortType.length === 1) {
    onChange?.(item.sortField, item.sortType[0])
    return
  }

  if (value?.field !== item.sortField) {
    onChange?.(item.sortField, 'desc')
    return
  }

  // 修改这里的逻辑，使其在 desc 和 asc 之间切换
  const nextType = value.type === 'desc' ? 'asc' : 'desc'
  onChange?.(item.sortField, nextType)
}

export const Sort = {
  Root: ({ children, value, onChange }: Omit<SortBaseProps, 'sorts'> & { children: React.ReactNode }) => {
    const contextValue = { value, onChange }

    return <SortContext.Provider value={contextValue}>{children}</SortContext.Provider>
  },

  Inline: ({ sorts, className, value: propValue, onChange: propOnChange }: SortBaseProps) => {
    const { value = propValue, onChange = propOnChange } = useContext(SortContext)

    return (
      <div className={cn('inline-flex items-center ', className)}>
        {sorts.map((item) => (
          <button
            key={item.sortField}
            className={cn(
              'inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-hover hover:text-accent transition-colors',
              value?.field === item.sortField && 'text-accent',
            )}
            onClick={() => onSortClick(item, value, onChange)}
          >
            <span>{item.label}</span>
            {item.sortType.length > 1 && (
              <div className="flex flex-col">
                <TriangleUpIcon
                  className={cn(
                    'mb-[-4px] text-muted-foreground',
                    value?.field === item.sortField && value?.type === 'asc' && 'text-accent',
                  )}
                />
                <TriangleDownIcon
                  className={cn(
                    'text-muted-foreground -mt-1',
                    value?.field === item.sortField && value?.type === 'desc' && 'text-accent',
                  )}
                />
              </div>
            )}
          </button>
        ))}
      </div>
    )
  },

  Dropdown: ({
    sorts,
    className,
    placeholder,
    withPlaceholder,
    value: propValue,
    onChange: propOnChange,
    disabled,
  }: SortBaseProps) => {
    const { value = propValue, onChange = propOnChange } = useContext(SortContext)

    const currentValue = value?.field || sorts[0].sortField
    const display = sorts.find((f) => f.sortField === currentValue)
    const label = display?.label || placeholder
    return (
      <Combobox
        className={cn({ 'text-accent': !!display }, className)}
        disabled={disabled}
        allowClear={false}
        placeholder={placeholder || '排序'}
        showSearch={false}
        value={currentValue}
        onValueChange={(newValue) => {
          const selectedItem = sorts.find((item) => item.sortField === newValue)
          if (selectedItem) {
            onSortClick(selectedItem, value, onChange)
          }
        }}
      >
        <Combobox.Display>
          <div>{withPlaceholder ? [placeholder, display?.label].filter(Boolean).join('：') : label}</div>
        </Combobox.Display>
        {sorts.map((v) => (
          <Combobox.Option key={v.sortField} value={v.sortField}>
            {v.label}
          </Combobox.Option>
        ))}
      </Combobox>
    )
  },

  Sheet: ({ sorts = [], className, placeholder, value: propValue, onChange: propOnChange }: SortBaseProps) => {
    const { value = propValue, onChange = propOnChange } = useContext(SortContext)

    const [open, setOpen] = useState(false)
    const [tempValue, setTempValue] = useState(value)
    const displayText = sorts?.find((s) => s.sortField === value?.field)?.label
    const isSelected = !!sorts?.find((s) => s.sortField === tempValue?.field)
    return (
      <Popover
        open={open}
        onOpenChange={(_open) => {
          setOpen(_open)
          if (_open) {
            setTempValue(value)
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            className={cn(
              'flex items-center border-none shadow-none h-8 w-fit justify-between rounded px-2 py-1 hover:bg-hover active:bg-active',
              { 'text-accent': displayText },
              className,
            )}
          >
            <span className={'truncate'}>
              {displayText ? `${displayText}${value?.type === 'desc' ? '最多' : '最少'}` : placeholder || '排序'}
            </span>
            <ChevronDown size={16} className={cn('ml-2 shrink-0 transition-transform duration-200', open && 'rotate-180')} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandList>
              <div className="max-h-80 overflow-auto p-1">
                <CommandGroup>
                  {sorts?.map((item, index) => (
                    <CommandItem
                      key={item.sortField}
                      onSelect={() => {
                        setTempValue((prev) =>
                          prev ? { ...prev, field: item.sortField, type: prev.type } : { field: item.sortField, type: 'desc' },
                        )
                      }}
                      className="px-2 py-1.5"
                    >
                      <Check
                        className={cn(
                          'h-4 w-4',
                          tempValue?.field === item.sortField || (!isSelected && index === 0) ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {item.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <Separator orientation="horizontal" className="w-auto m-1" />
                <CommandGroup>
                  <CommandItem
                    onSelect={() =>
                      setTempValue((prev) => (prev ? { ...prev, type: 'desc' } : { field: sorts[0].sortField, type: 'desc' }))
                    }
                    className="px-2 py-1.5"
                  >
                    <Check className={cn('mr-2 h-4 w-4', tempValue?.type === 'desc' ? 'opacity-100' : 'opacity-0')} />
                    最多
                  </CommandItem>
                  <CommandItem
                    onSelect={() =>
                      setTempValue((prev) => (prev ? { ...prev, type: 'asc' } : { field: sorts[0].sortField, type: 'asc' }))
                    }
                    className="px-2 py-1.5"
                  >
                    <Check className={cn('mr-2 h-4 w-4', tempValue?.type === 'asc' ? 'opacity-100' : 'opacity-0')} />
                    最少
                  </CommandItem>
                </CommandGroup>
              </div>
            </CommandList>
            <div className="flex items-center justify-end gap-2 border-t border-active py-3 px-4">
              <Button
                className="w-20 h-8"
                variant="secondary"
                onClick={() => {
                  setOpen(false)
                  setTempValue(value)
                }}
              >
                取消
              </Button>
              <Button
                className="w-20 h-8"
                onClick={() => {
                  if (isSelected) {
                    onChange?.(tempValue?.field || '', tempValue?.type || 'desc')
                  } else {
                    onChange?.(sorts[0].sortField, 'desc')
                  }
                  setOpen(false)
                }}
              >
                确定
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
}
